import YAML from 'yaml'
import { createLogger } from './logger'

const log = createLogger('DocsManifest')

type FrontMatterInput = {
  title?: string
  navTitle?: string
  category?: string
  categoryOrder?: number
  order?: number
  summary?: string
  status?: 'idea' | 'draft' | 'in-review' | 'published'
  tags?: string[]
  updated?: string
  slug?: string
  hidden?: boolean
}

export type DocFrontMatter = {
  title: string
  navTitle: string
  category: string
  categoryOrder: number
  order: number
  summary?: string
  status: 'idea' | 'draft' | 'in-review' | 'published'
  tags: string[]
  updated?: string
  hidden: boolean
}

export type DocManifestItem = {
  slug: string
  path: string
  content: string
  frontMatter: DocFrontMatter
}

export type DocManifestSection = {
  key: string
  title: string
  order: number
  items: DocManifestItem[]
}

const rawDocs = import.meta.glob('/docs/*.md', {
  import: 'default',
  query: '?raw',
  eager: true,
}) as Record<string, string>

const parseDocFile = (raw: string): { data: FrontMatterInput; content: string } => {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?/)
  if (!match) {
    return { data: {}, content: raw }
  }

  const [, frontMatterBlock] = match
  let data: FrontMatterInput = {}

  try {
    const parsed = YAML.parse(frontMatterBlock)
    if (parsed && typeof parsed === 'object') {
      data = parsed as FrontMatterInput
    }
  } catch (error) {
    log.error('Failed to parse front matter', error)
  }

  const content = raw.slice(match[0].length)
  return { data, content }
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const fallbackTitleFromPath = (path: string) => {
  const fileName = path.split('/').pop() ?? path
  const withoutExt = fileName.replace(/\.md$/i, '')
  return withoutExt.replace(/[-_]/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const normalizeFrontMatter = (path: string, data: FrontMatterInput): DocFrontMatter => {
  const title = (data.title ?? fallbackTitleFromPath(path)).trim()
  const navTitle = (data.navTitle ?? title).trim()
  const category = (data.category ?? 'General').trim()
  const categoryOrder = Number.isFinite(data.categoryOrder) ? Number(data.categoryOrder) : 100
  const order = Number.isFinite(data.order) ? Number(data.order) : 100
  const tags = Array.isArray(data.tags) ? data.tags.filter((tag) => typeof tag === 'string') : []
  const status = (data.status ?? 'published') as DocFrontMatter['status']
  const updated = data.updated?.trim()
  const hidden = Boolean(data.hidden)

  return {
    title,
    navTitle,
    category,
    categoryOrder,
    order,
    summary: data.summary?.trim(),
    status,
    tags,
    updated,
    hidden,
  }
}

const docsList: DocManifestItem[] = Object.entries(rawDocs).map(([path, raw]) => {
  const parsed = parseDocFile(raw)
  const frontMatter = normalizeFrontMatter(path, parsed.data)
  const slug = (parsed.data.slug as string | undefined)?.trim() ?? slugify(frontMatter.navTitle)

  return {
    slug,
    path,
    content: parsed.content.trim(),
    frontMatter,
  }
})

const visibleDocs = docsList.filter((item) => !item.frontMatter.hidden)

const sectionsMap = new Map<string, DocManifestSection>()

visibleDocs.forEach((item) => {
  const { category, categoryOrder } = item.frontMatter
  if (!sectionsMap.has(category)) {
    sectionsMap.set(category, {
      key: slugify(category) || 'general',
      title: category,
      order: categoryOrder,
      items: [],
    })
  }
  sectionsMap.get(category)!.items.push(item)
})

const docsSections = Array.from(sectionsMap.values())
  .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
  .map((section) => ({
    ...section,
    items: section.items.sort(
      (a, b) =>
        a.frontMatter.order - b.frontMatter.order ||
        a.frontMatter.title.localeCompare(b.frontMatter.title),
    ),
  }))

const docsIndex = visibleDocs.reduce<Record<string, DocManifestItem>>((acc, item) => {
  acc[item.slug] = item
  return acc
}, {})

export const docSections: readonly DocManifestSection[] = docsSections
export const docsBySlug: Readonly<Record<string, DocManifestItem>> = docsIndex
export const allDocs: readonly DocManifestItem[] = visibleDocs
